/**
 * Wave-phase logic. Spawns creeps along the path, advances them each tick,
 * applies leak / death, ends the wave when all creeps are dead or leaked.
 */

import { Game } from '../game/Game';
import { CreepState, CreepPayload, creepDeathMetrics } from '../game/State';
import { CREEP_ARCHETYPES } from '../data/creeps';
import { WAVES, type WaveDef, type PayloadGroup, waveTotalCount, groupForSpawn } from '../data/waves';
import { generateWave } from '../data/wave-generator';
import { FINE_TILE, GRID_SCALE, SIM_DT, SIM_HZ, TILE } from '../game/constants';

const HEALER_INTERVAL = 5 * SIM_HZ;
const HEALER_RADIUS_PX = 3.5 * TILE;
const HEALER_BUFF_DURATION = 2 * SIM_HZ;
const HEALER_HEAL_PCT = 0.00095;

const WIZARD_COOLDOWN = 12 * SIM_HZ;
const WIZARD_RADIUS_PX = 3 * TILE;
const WIZARD_TELEPORT_TILES = 8;

const TUNNELER_COOLDOWN = 15 * SIM_HZ;
const TUNNELER_BURROW_DURATION = 2.5 * SIM_HZ;
const TUNNELER_BURROW_SPEED_MULT = 1.0;
const TUNNELER_BURROW_HEAL_PER_TICK = 0.0003;

const CHRYSALID_HP_THRESHOLD = 0.4;
const CHRYSALID_SPEED_MULT = 1.6;
const CHRYSALID_AWAKEN_ARMOR = 10;

const MYCOID_PULSE_COOLDOWN = 4 * SIM_HZ;
const MYCOID_PULSE_RADIUS_PX = 2.5 * TILE;
const MYCOID_SILENCE_DURATION = 3 * SIM_HZ;

const GESTATION_HP_THRESHOLD = 0.5;

export class WavePhase {
  private wave = 0;
  private spawnedSoFar = 0;
  private spawnTimer = 0;
  /** seconds since wave start */
  private elapsed = 0;
  private goldEarned = 0;
  private livesAtStart = 0;
  private generatedWaveCache = new Map<number, WaveDef>();

  onEnter(wave: number): void {
    this.wave = wave;
    this.spawnedSoFar = 0;
    this.spawnTimer = 0;
    this.elapsed = 0;
    this.goldEarned = 0;
    this.livesAtStart = this.game.state.lives;
    const def = this.waveDef()!;
    this.game.state.waveStats = {
      spawnedThisWave: 0,
      killedThisWave: 0,
      leakedThisWave: 0,
      totalToSpawn: waveTotalCount(def),
    };
  }

  constructor(private game: Game) {}

  clearGeneratedCache(): void {
    this.generatedWaveCache.clear();
  }

  private waveDef(): WaveDef | undefined {
    if (this.game.state.debugWaveDef) return this.game.state.debugWaveDef;
    if (this.wave <= WAVES.length) return WAVES[this.wave - 1];
    return this.getGeneratedWave(this.wave);
  }

  private getGeneratedWave(waveNum: number): WaveDef {
    let cached = this.generatedWaveCache.get(waveNum);
    if (!cached) {
      cached = generateWave(waveNum, this.game.seed);
      this.generatedWaveCache.set(waveNum, cached);
    }
    return cached;
  }

  step(): void {
    const state = this.game.state;
    if (state.phase !== 'wave') return;
    const def = this.waveDef();
    if (!def) return;

    this.elapsed += SIM_DT;
    this.spawnTimer += SIM_DT;

    const total = waveTotalCount(def);

    // Spawn creeps according to interval.
    if (this.spawnedSoFar < total && this.spawnTimer >= def.interval) {
      this.spawnTimer = 0;
      this.spawnCreep();
    } else if (this.spawnedSoFar < total && this.spawnedSoFar === 0) {
      // Spawn the first one immediately.
      this.spawnCreep();
    }

    // Advance creeps.
    for (const c of state.creeps) {
      if (!c.alive) continue;
      this.advanceCreep(c);
      if (state.phase !== 'wave') return;
    }

    // Creep abilities.
    for (const c of state.creeps) {
      if (!c.alive) continue;
      this.tickAbility(c);
    }

    // Prune dead creeps; if all dead AND all spawned, end the wave.
    let write = 0;
    for (let i = 0; i < state.creeps.length; i++) {
      if (state.creeps[i].alive || this.recentlyDied(state.creeps[i])) {
        state.creeps[write++] = state.creeps[i];
      }
    }
    state.creeps.length = write;
    if (this.spawnedSoFar >= total && state.creeps.every((c) => !c.alive)) {
      this.endWave();
    }
  }

  private recentlyDied(_c: CreepState): boolean {
    // Could keep dying corpses for fadeout — for now, just drop them.
    return false;
  }

  private routeFor(isAir: boolean): Array<{ x: number; y: number }> {
    return isAir ? this.game.state.airRoute : this.game.state.flatRoute;
  }

  private spawnCreep(): void {
    const def = this.waveDef()!;
    const group = groupForSpawn(def, this.spawnedSoFar);
    const arch = CREEP_ARCHETYPES[group.kind];
    const isAir = !!arch.flags.air;
    const route = this.routeFor(isAir);
    if (route.length === 0) return;
    const start = route[0];
    const id = this.game.nextId();
    const hp = Math.round(group.hp * arch.hpMult);
    const creep: CreepState = {
      id,
      kind: arch.kind,
      pathPos: 0,
      px: start.x * FINE_TILE + FINE_TILE / 2,
      py: start.y * FINE_TILE + FINE_TILE / 2,
      hp,
      maxHp: hp,
      speed: arch.speed,
      bounty: Math.round(group.bounty * arch.bountyMult),
      color: arch.color,
      alive: true,
      armorReduction: 0,
      armor: group.armor ?? arch.defaultArmor ?? 0,
      slowResist: group.slowResist,
      stunResist: group.stunResist ?? 0,
      poisonResist: 0,
      flags: { ...arch.flags },
      vulnerability: 0,
      payload: group.payload ? resolvePayload(group.payload) : undefined,
    };
    creep.spawnTick = this.game.state.tick;
    this.game.state.creeps.push(creep);
    this.spawnedSoFar++;
    this.game.state.waveStats.spawnedThisWave++;
    this.game.bus.emit('creep:spawn', { id, kind: arch.kind, maxHp: hp });
  }

  private advanceCreep(c: CreepState): void {
    if (c.stun && c.stun.expiresAt > this.game.state.tick) return;
    let speed = c.speed;
    if (c.burrowed && c.burrowed.expiresAt > this.game.state.tick) {
      speed *= TUNNELER_BURROW_SPEED_MULT;
      c.hp = Math.min(c.maxHp, c.hp + Math.max(1, Math.round(c.maxHp * TUNNELER_BURROW_HEAL_PER_TICK)));
    }
    if (c.slow && c.slow.expiresAt > this.game.state.tick) {
      speed *= c.slow.factor;
    }
    if (c.proxSlowFactor !== undefined) {
      speed *= c.proxSlowFactor;
    }
    const isAir = !!c.flags?.air;
    const route = this.routeFor(isAir);
    if (route.length === 0) return;
    c.pathPos += speed * GRID_SCALE * SIM_DT;
    if (c.pathPos >= route.length - 1) {
      c.alive = false;
      this.leak(c);
      return;
    }
    const i = Math.floor(c.pathPos);
    const tA = route[i];
    const tB = route[i + 1] ?? tA;
    const frac = c.pathPos - i;
    c.px = (tA.x + (tB.x - tA.x) * frac) * FINE_TILE + FINE_TILE / 2;
    c.py = (tA.y + (tB.y - tA.y) * frac) * FINE_TILE + FINE_TILE / 2;

    // Clear expired buffs/states
    if (c.stun && c.stun.expiresAt <= this.game.state.tick) c.stun = undefined;
    if (c.slow && c.slow.expiresAt <= this.game.state.tick) c.slow = undefined;
    if (c.poison && c.poison.expiresAt <= this.game.state.tick) c.poison = undefined;
    if (c.burrowed && c.burrowed.expiresAt <= this.game.state.tick) c.burrowed = undefined;
    if (c.healBuff && c.healBuff.expiresAt <= this.game.state.tick) c.healBuff = undefined;
    if (c.armorDebuff && c.armorDebuff.expiresAt <= this.game.state.tick) c.armorDebuff = undefined;
    if (c.afterburn && c.afterburn.expiresAt <= this.game.state.tick) c.afterburn = undefined;

    // Heal buff ticks
    if (c.healBuff) {
      c.hp = Math.min(c.maxHp, c.hp + c.healBuff.hpPerTick);
    }

    if (c.hp <= 0) {
      this.kill(c);
      return;
    }

    // Chrysalid awakening: when HP drops below threshold, gain high resistances + speed boost
    if (c.kind === 'chrysalid' && !c.chrysalidAwakened && c.hp / c.maxHp <= CHRYSALID_HP_THRESHOLD) {
      c.chrysalidAwakened = true;
      c.speed *= CHRYSALID_SPEED_MULT;
      c.armor += CHRYSALID_AWAKEN_ARMOR;
      c.slowResist = 0.7;
      c.stunResist = 0.7;
      c.poisonResist = 0.7;
      c.slow = undefined;
      c.stun = undefined;
      c.poison = undefined;
      c.armorDebuff = undefined;
      this.game.bus.emit('creep:chrysalidAwaken', { id: c.id });
    }

    // Gestation enrage: at 50% HP, transform and begin pulsing
    if (c.kind === 'gestation' && !c.gestationEnraged && c.hp / c.maxHp <= GESTATION_HP_THRESHOLD) {
      c.gestationEnraged = true;
      this.game.bus.emit('creep:gestationEnrage', { id: c.id });
      this.game.bus.emit('vfx:gestationTransition', { x: c.px, y: c.py });
    }
  }

  private leak(c: CreepState): void {
    const state = this.game.state;
    const waveNum = state.wave;
    const baseCost = c.flags?.boss ? 5 : 1;
    const rawCost = baseCost + Math.floor(waveNum / 10);
    const cost = c.flags?.boss ? rawCost : Math.min(4, rawCost);
    state.lives = Math.max(0, state.lives - cost);
    state.waveStats.leakedThisWave++;
    const { ticksAlive } = creepDeathMetrics(c, state);
    this.game.bus.emit('creep:leak', { id: c.id, kind: c.kind, hp: c.hp, maxHp: c.maxHp, liveCost: cost, ticksAlive });
    this.game.bus.emit('lives:change', { lives: state.lives });
    if (state.lives <= 0) {
      this.endWave();
    }
  }

  private kill(c: CreepState): void {
    const state = this.game.state;
    c.alive = false;
    state.gold += c.bounty;
    state.totalKills++;
    state.waveStats.killedThisWave++;
    this.goldEarned += c.bounty;
    const { pathProgress, ticksAlive } = creepDeathMetrics(c, state);
    this.game.bus.emit('creep:die', { id: c.id, kind: c.kind, bounty: c.bounty, pathProgress, ticksAlive });
    this.game.bus.emit('gold:change', { gold: state.gold });
    this.game.handleCreepDeath(c);
  }

  private tickAbility(c: CreepState): void {
    const tick = this.game.state.tick;
    if (c.stun && c.stun.expiresAt > tick) return;
    if (c.abilityCooldown && tick < c.abilityCooldown) return;

    switch (c.kind) {
      case 'mender':
        this.healerAbility(c, tick);
        break;
      case 'wizard':
        this.wizardAbility(c, tick);
        break;
      case 'burrower':
        this.tunnelerAbility(c, tick);
        break;
      case 'mycoid':
        this.mycoidAbility(c, tick);
        break;
    }
  }

  private healerAbility(c: CreepState, tick: number): void {
    c.abilityCooldown = tick + HEALER_INTERVAL;
    const state = this.game.state;
    const r2 = HEALER_RADIUS_PX * HEALER_RADIUS_PX;
    for (const other of state.creeps) {
      if (!other.alive || other.id === c.id) continue;
      const dx = other.px - c.px;
      const dy = other.py - c.py;
      if (dx * dx + dy * dy > r2) continue;
      if (other.healBuff && other.healBuff.expiresAt > tick) continue;
      const hpPerTick = Math.max(1, Math.round(other.maxHp * HEALER_HEAL_PCT));
      other.healBuff = { hpPerTick, expiresAt: tick + HEALER_BUFF_DURATION };
    }
  }

  private wizardAbility(c: CreepState, tick: number): void {
    c.abilityCooldown = tick + WIZARD_COOLDOWN;
    const state = this.game.state;
    const r2 = WIZARD_RADIUS_PX * WIZARD_RADIUS_PX;
    const wpPositions = this.waypointPositions();
    for (const other of state.creeps) {
      if (!other.alive || other.id === c.id) continue;
      if (other.flags?.air) continue;
      const dx = other.px - c.px;
      const dy = other.py - c.py;
      if (dx * dx + dy * dy > r2) continue;
      const nextWp = wpPositions.find((wp) => wp > other.pathPos);
      if (nextWp === undefined) continue;
      const maxAdvance = nextWp - other.pathPos;
      const advance = Math.min(WIZARD_TELEPORT_TILES, maxAdvance);
      other.pathPos += advance;
    }
  }

  private tunnelerAbility(c: CreepState, tick: number): void {
    if (c.burrowed && c.burrowed.expiresAt > tick) return;
    c.abilityCooldown = tick + TUNNELER_COOLDOWN;
    c.burrowed = { expiresAt: tick + TUNNELER_BURROW_DURATION };
  }

  private mycoidAbility(c: CreepState, tick: number): void {
    c.abilityCooldown = tick + MYCOID_PULSE_COOLDOWN;
    const state = this.game.state;
    const r2 = MYCOID_PULSE_RADIUS_PX * MYCOID_PULSE_RADIUS_PX;
    const silenceEnd = tick + MYCOID_SILENCE_DURATION;
    for (const t of state.towers) {
      const tx = (t.x + 1) * FINE_TILE;
      const ty = (t.y + 1) * FINE_TILE;
      const dx = tx - c.px;
      const dy = ty - c.py;
      if (dx * dx + dy * dy > r2) continue;
      t.silencedUntil = Math.max(t.silencedUntil ?? 0, silenceEnd);
    }
    this.game.bus.emit('vfx:mycoidPulse', { x: c.px, y: c.py, radiusPx: MYCOID_PULSE_RADIUS_PX });
  }


  private waypointPositions(): number[] {
    const segs = this.game.state.routeSegments;
    const positions: number[] = [];
    let cumLen = 0;
    for (let i = 0; i < segs.length; i++) {
      cumLen += segs[i].length - (i > 0 ? 1 : 0);
      positions.push(cumLen - 1);
    }
    return positions;
  }

  private endWave(): void {
    const state = this.game.state;
    const def = this.waveDef()!;
    const lifeLost = this.livesAtStart - state.lives;
    if (state.waveStats.leakedThisWave === 0) {
      state.gold += def.bonus;
      this.goldEarned += def.bonus;
    }
    if (state.wave >= 30) {
      const bonus = Math.round(this.goldEarned * 0.18);
      state.gold += bonus;
      this.goldEarned += bonus;
    }
    this.game.bus.emit('gold:change', { gold: state.gold });
    this.game.endWave(lifeLost, this.goldEarned);
  }
}

function resolvePayload(groups: PayloadGroup[]): CreepPayload[] {
  return groups.map(g => {
    const arch = CREEP_ARCHETYPES[g.kind];
    return {
      kind: g.kind,
      count: g.count,
      hp: Math.round(g.hp * arch.hpMult),
      speed: arch.speed,
      bounty: Math.round(g.bounty * arch.bountyMult),
      color: arch.color,
      armor: g.armor ?? arch.defaultArmor ?? 0,
      slowResist: g.slowResist ?? 0,
      stunResist: g.stunResist ?? 0,
      poisonResist: 0,
      flags: { ...arch.flags },
      payload: g.payload ? resolvePayload(g.payload) : undefined,
    };
  });
}
